import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { api } from '../lib/api';

const AMAZON_EMAIL_RE = /@(?:[a-z0-9-]+\.)*amazon(?:\.[a-z]{2,})+$/i;

const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'guerrillamail.net', 'tempmail.com',
  'throwaway.email', 'temp-mail.org', 'fakeinbox.com', 'sharklasers.com',
  'guerrillamailblock.com', 'grr.la', 'dispostable.com', 'yopmail.com',
  'trashmail.com', 'trashmail.net', 'maildrop.cc', 'mailnesia.com',
  'tempr.email', 'discard.email', 'tempail.com', 'mohmal.com',
  'getnada.com', 'emailondeck.com', '10minutemail.com', 'minutemail.com',
  'temp-mail.io', 'tempmailo.com', 'burnermail.io', 'mailcatch.com',
  'mytemp.email', 'dropmail.me', 'harakirimail.com',
  'tmail.ws', 'crazymailing.com', 'mailforspam.com', 'spamgourmet.com',
]);

interface PublicAoc {
  id: string;
  friendlyName: string;
  reportDate: string;
}

export function SignUpPage() {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '',
    email: '',
    organization: '',
    reason: '',
    phoneNumber: '(+1) ',
  });
  const [ndaAcknowledged, setNdaAcknowledged] = useState(false);
  const [notifyNewDocs, setNotifyNewDocs] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [aocs, setAocs] = useState<PublicAoc[]>([]);
  const [selectedAocs, setSelectedAocs] = useState<Set<string>>(new Set());
  const [aocsLoading, setAocsLoading] = useState(true);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaRendered = useRef(false);

  useEffect(() => {
    if (captchaRendered.current || !captchaContainerRef.current) return;
    captchaRendered.current = true;

    const WAF_INTEGRATION_URL = 'https://64e3289d96dc.edge.captcha-sdk.awswaf.com/64e3289d96dc/jsapi.js';
    const WAF_API_KEY = 'fBSd90HaGm/AjExKY5DFJos+L4KqroqYkUD202zCTfBT+Ywa3lSTu8/s/60+S/ng/J9mcTg8tDJ7Pa77Wbj3AEAYwMW+NNcQkPFSjulTbf+K2hxwp3EHPMhpr6v/kZpCIEtpSeNrYxFkjf3UjcF+ROSRc3gGk9EU+FKVdsOpfahKomSnmNNeqevDFJ/BCFYnz3zI7BTn5PIRtJOIZjSB+5YLFnP/s0NOy0jTN2yWF+tQLiwVXB9uE1hBUTQsulDiKiqlIjOHXtTrPNWTjk4Fj3Tbuw5YQVl/yHzlmUEX8/SdAjQMw6FSwGWrMPx0jZWe5mOcjLir0rNrV0rgntp9cmTUgv2Jaldmn2EZ9UMAtc463ucte8KpJbzxRcdybV0LbWSwKBU4ET2LrPfUFl+x0xPUIoq2tlfBtzmH6mVqpsYrTHOJ5Udco5D8f9x56mlrn5rEM9fPaX8RR01dS1jqrAjeRDw38cVT01VYCtUIDGFC3kPEhoUMgzuzz5EAQfI02w3QoluD49MNa3MLK/T7gihH9vvmDnMA9cW8WAGZ6Df72eGpe5kLmc1dHjGJeoKB8OzjomorMoFQDe3+Osv3d/WgrG8GXQxS0VikDpufxprbwshRzrzoAEM/+KnovOcIscK5iovdy4Ux+U7jVxnigeNs3MXtEZbSzo841XBMlVM=_1_1';

    const script = document.createElement('script');
    script.src = WAF_INTEGRATION_URL;
    script.async = true;
    script.onload = () => {
      const AwsWafCaptcha = (window as any).AwsWafCaptcha;
      if (!AwsWafCaptcha || !captchaContainerRef.current) return;
      AwsWafCaptcha.renderCaptcha(captchaContainerRef.current, {
        apiKey: WAF_API_KEY,
        onSuccess: (token: string) => {
          setCaptchaToken(token);
        },
        onError: () => {
          setCaptchaToken(null);
        },
      });
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listPublicAocs();
        if (!cancelled) setAocs(data.aocs || []);
      } catch (err) {
        console.warn('Failed to load AOC catalog for signup:', err);
      } finally {
        if (!cancelled) setAocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAmazonEmail = useMemo(
    () => AMAZON_EMAIL_RE.test(form.email.trim()),
    [form.email],
  );

  const isDisposableEmail = useMemo(() => {
    const domain = form.email.trim().toLowerCase().split('@')[1];
    return domain ? DISPOSABLE_DOMAINS.has(domain) : false;
  }, [form.email]);

  function toggleAoc(id: string) {
    setSelectedAocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isAmazonEmail) {
      setError(t('signup.amazonEmailError'));
      return;
    }
    if (isDisposableEmail) {
      setError(t('signup.disposableEmailError'));
      return;
    }
    if (!ndaAcknowledged) {
      setError(t('signup.ndaError'));
      return;
    }

    setLoading(true);
    try {
      const AwsWafIntegration = (window as any).AwsWafIntegration;
      const fetchFn = AwsWafIntegration?.fetch ? AwsWafIntegration.fetch.bind(AwsWafIntegration) : fetch;
      const res = await fetchFn('/api/auth/idprism/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          name: form.name,
          organization: form.organization,
          reason: form.reason,
          requestedAocReportIds: Array.from(selectedAocs),
          phoneNumber: (() => {
            const raw = form.phoneNumber.trim();
            if (!raw || raw === '(+1)' || raw === '(+1) ') return undefined;
            // Convert display format to E.164: strip parens, spaces, dashes
            const digits = raw.replace(/[()\s-]/g, '');
            return digits.startsWith('+') ? digits : '+' + digits;
          })(),
          ndaAcknowledged: true,
          notifyNewDocuments: notifyNewDocs,
        }),
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const body = await res.json().catch(() => ({ error: res.statusText }));
          throw new Error(body.error || `Request failed: ${res.status}`);
        }
        if (res.status === 405 || res.status === 403) {
          throw new Error(t('signup.captchaBlockedError', { defaultValue: 'Request blocked by security check. Please refresh the page and complete the CAPTCHA again.' }));
        }
        throw new Error(`Request failed: ${res.status}`);
      }
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('signup.signUpFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>{t('signup.success.title')}</h1>
          <p className="login-subtitle">
            {t('signup.success.message')}
          </p>
          <p className="login-subtitle">
            {t('signup.success.approvalNote')}
          </p>

          <a href="/auth/idprism/login" className="btn-primary" style={{ display: 'block', textAlign: 'center', width: '100%' }}>
            {t('signup.success.signInButton')}
          </a>

          <div className="login-links" style={{ textAlign: 'center' }}>
            <Link to="/login">{t('signup.success.backToLogin')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card login-card-wide">
        <h1>{t('signup.title')}</h1>
        <p className="login-subtitle">
          {t('signup.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="name">{t('signup.fullName')}</label>
            <input
              id="name"
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              autoComplete="name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">{t('signup.workEmail')}</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              autoComplete="email"
              aria-invalid={isAmazonEmail || undefined}
              aria-describedby={isAmazonEmail ? 'email-amazon-notice' : undefined}
            />
            {isAmazonEmail && (
              <div id="email-amazon-notice" className="form-info" role="alert">
                {t('signup.amazonEmailNotice')}{' '}
                <Link to="/login">{t('signup.amazonEmailLink')}</Link>{' '}
                <Trans i18nKey="signup.amazonEmailSuffix" />
              </div>
            )}
            {isDisposableEmail && (
              <div className="form-error" role="alert">
                {t('signup.disposableEmailError')}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="organization">{t('common.organization')}</label>
            <input
              id="organization"
              type="text"
              value={form.organization}
              onChange={(e) => setForm({ ...form, organization: e.target.value })}
              required
              placeholder={t('signup.orgPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label htmlFor="reason">{t('signup.reasonLabel')}</label>
            <textarea
              id="reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              placeholder={t('signup.reasonPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label htmlFor="phoneNumber">{t('signup.phoneLabel')}</label>
            <input
              id="phoneNumber"
              type="tel"
              value={form.phoneNumber}
              onChange={(e) => {
                let val = e.target.value;
                // Extract country code from parentheses
                const codeMatch = val.match(/^\(\+(\d{1,4})\)\s*/);
                if (codeMatch && codeMatch[1] === '1') {
                  // US format: (+1) XXX-XXX-XXXX
                  const afterCode = val.slice(codeMatch[0].length).replace(/[^\d]/g, '').slice(0, 10);
                  let formatted = '(+1) ';
                  if (afterCode.length > 0) formatted += afterCode.slice(0, 3);
                  if (afterCode.length > 3) formatted += '-' + afterCode.slice(3, 6);
                  if (afterCode.length > 6) formatted += '-' + afterCode.slice(6);
                  val = formatted;
                }
                setForm({ ...form, phoneNumber: val });
              }}
              placeholder="(+1) 555-123-4567"
              autoComplete="tel"
            />
            <p className="form-hint">{t('signup.phoneHint')}</p>
          </div>

          <div className="form-group">
            <fieldset className="aoc-fieldset">
              <legend>{t('signup.aocsLegend')}</legend>
              <p className="form-hint">
                {t('signup.aocsHint')}
              </p>
              {aocsLoading ? (
                <p className="form-hint">{t('signup.aocsLoading')}</p>
              ) : aocs.length === 0 ? (
                <p className="form-hint">{t('signup.aocsEmpty')}</p>
              ) : (
                <div className="aoc-checklist" role="group" aria-label="AOC selection">
                  {aocs.map((aoc) => {
                    const checked = selectedAocs.has(aoc.id);
                    const dateLabel = aoc.reportDate
                      ? new Date(aoc.reportDate.slice(0, 10) + 'T00:00:00').toLocaleDateString()
                      : null;
                    return (
                      <label key={aoc.id} className="aoc-check-row">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAoc(aoc.id)}
                        />
                        <span className="aoc-check-label">
                          {aoc.friendlyName || aoc.id}
                          {dateLabel && <span className="aoc-check-date"> · {dateLabel}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>
          </div>

          {error && <div className="form-error" role="alert">{error}</div>}

          <div className="form-group">
            <label className="aoc-check-row">
              <input
                type="checkbox"
                checked={notifyNewDocs}
                onChange={(e) => setNotifyNewDocs(e.target.checked)}
              />
              <span className="aoc-check-label">
                {t('signup.notifyLabel', 'Notify me when new compliance documents are available')}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label className="aoc-check-row">
              <input
                type="checkbox"
                checked={ndaAcknowledged}
                onChange={(e) => setNdaAcknowledged(e.target.checked)}
                required
              />
              <span className="aoc-check-label">
                {t('signup.ndaLabel')}
              </span>
            </label>
          </div>

          <div className="form-group">
            <div ref={captchaContainerRef} id="waf-captcha" />
          </div>

          <button type="submit" disabled={loading || isAmazonEmail || isDisposableEmail || !captchaToken} className="btn-primary">
            {loading ? t('signup.submitting') : t('signup.submit')}
          </button>

          <div className="login-links">
            <Link to="/login">{t('signup.alreadyHaveAccount')}</Link>
          </div>
        </form>

        <div className="login-footer">
          <p className="contact-info">
            {t('common.questionsContact')} <a href="mailto:pci-contact@amazon.com">pci-contact@amazon.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
